import { X, Check } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { Button } from "./Button";
import { toast, type Position } from "../ui/Sonner";

type AlertVariant = "default" | "success" | "warning" | "error" | "info";
type AlertStyle = "outlined" | "contained";
type AlertType = "simple" | "with-actions";

const variantConfig = {
  default: {
    outlined: {
      container: "bg-white border-grey-200",
      icon: "text-white bg-grey-800 rounded-full p-1",
      xIcon: "text-grey-800",
      title: "text-black font-semibold text-sm",
      description: "text-grey-400 text-xs font-semibold",
    },
    contained: {
      container: "bg-black",
      icon: "text-black bg-white rounded-full p-1",
      xIcon: "text-white",
      title: "text-white font-semibold text-sm",
      description: "text-grey-200 text-xs font-semibold",
    },
  },
  success: {
    outlined: {
      container: "bg-success-bg-alert border-success-bg-hover",
      icon: "text-white bg-success-700 rounded-full p-1",
      xIcon: "text-success-700",
      title: "text-success-700 font-semibold text-sm",
      description: "text-success-600 text-xs font-semibold",
    },
    contained: {
      container: "bg-success-700",
      icon: "text-success-700 bg-white rounded-full p-1",
      xIcon: "text-white",
      title: "text-white font-semibold text-sm",
      description: "text-white text-xs font-semibold",
    },
  },
  warning: {
    outlined: {
      container: "bg-warning-bg border-warning-bg-hover",
      icon: "text-white bg-warning-800 rounded-full p-1",
      xIcon: "text-warning-700",
      title: "text-warning-800 font-semibold text-sm",
      description: "text-warning-700 text-xs font-semibold",
    },
    contained: {
      container: "bg-warning-700",
      icon: "text-warning-700 bg-white rounded-full p-1",
      xIcon: "text-white",
      title: "text-white font-semibold text-sm",
      description: "text-white text-xs font-semibold",
    },
  },
  error: {
    outlined: {
      container: "bg-red-50 border-red-200",
      icon: "text-white bg-red-700 rounded-full p-1",
      xIcon: "text-red-700",
      title: "text-red-700 font-semibold text-sm",
      description: "text-red-500 text-xs font-semibold",
    },
    contained: {
      container: "bg-error-700",
      icon: "text-red-700 bg-white rounded-full p-1",
      xIcon: "text-white",
      title: "text-white font-semibold text-sm",
      description: "text-white text-xs font-semibold",
    },
  },
  info: {
    outlined: {
      container: "bg-blue-50 border-blue-200",
      icon: "text-white bg-blue-700 rounded-full p-1",
      xIcon: "text-blue-700",
      title: "text-blue-700 font-semibold text-sm",
      description: "text-blue-500 text-xs font-semibold",
    },
    contained: {
      container: "bg-neutral-700",
      icon: "text-blue-700 bg-white rounded-full p-1",
      xIcon: "text-white",
      title: "text-white font-semibold te6xt-sm",
      description: "text-white text-xs font-semibold",
    },
  },
};

// Base Alert Props
interface AlertProps {
  variant?: AlertVariant;
  style?: AlertStyle;
  type?: AlertType;
  title: string;
  description?: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  className?: string;
  iconComponent?: React.ReactNode;
  withXIcon?: boolean;
}

// Alert Component
export function Alert({
  variant = "default",
  style = "outlined",
  type = "simple",
  title,
  description,
  primaryAction,
  secondaryAction,
  onDismiss,
  className,
  iconComponent,
  withXIcon = true,
}: AlertProps) {
  const config = variantConfig[variant][style];
  
  // icon with config styling
  const renderIcon = () => {
    if (iconComponent) {
      return (
        <span className={cn("flex items-center justify-center h-5 w-5 flex-shrink-0", config.icon)}>
          {iconComponent}
        </span>
      );
    }
    return <Check className={cn("h-5 w-5 flex-shrink-0", config.icon)} />;
  };

  //  icon, title, description ,actions
  if (type === "with-actions") {
    return (
      <div
        className={cn(
          "flex flex-col gap-3 rounded-xl border p-6 shadow-lg",
          config.container,
          className
        )}
      >
        {withXIcon && onDismiss && (
          <button
            onClick={onDismiss}
            className={cn(
              "absolute top-3 right-3 transition-opacity hover:opacity-70",
              config.title
            )}
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="flex flex-col gap-2 pr-6">
          <div className="flex items-center gap-2">
            {renderIcon()}
            <h4 className={cn(config.title)}>{title}</h4>
          </div>

          {description && (
            <p className={cn("leading-relaxed min-w-[300px]", config.description)}>
              {description}
            </p>
          )}
        </div>

        {(primaryAction || secondaryAction) && (
          <div className="flex items-center gap-2">
            {primaryAction && (
              <Button
                size="sm"
                variant={style === "contained" ? "secondary" : "black"}
                className={cn(
                  style === "outlined" &&
                    variant === "success" &&
                    "bg-success-700  text-white hover:bg-success-800",
                  style === "outlined" &&
                    variant === "error" &&
                    "bg-error-700  text-white hover:bg-error-800",
                  style === "outlined" &&
                    variant === "warning" &&
                    "bg-warning-700 hover:bg-warning-800 text-white",
                  style === "outlined" &&
                    variant === "info" &&
                    "bg-neutral-700 hover:bg-neutral-700 text-white "
                )}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </Button>
            )}
            {secondaryAction && (
              <Button
                size="sm"
                variant="outline"
                className={cn(
                  style === "outlined" &&
                    variant === "success" &&
                    "text-success-700 border-success-700 ",
                  style === "outlined" &&
                    variant === "error" &&
                    "text-error-700 border-error-700 ",
                  style === "outlined" &&
                    variant === "warning" &&
                    "text-warning-700 border-warning-700 hover:bg-warning-50",
                  style === "outlined" &&
                    variant === "info" &&
                    "text-neutral-700 border-neutral-700 hover:bg-neutral-50",
                  
                )}
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  //  icon, title, description - single line layout
  if (type === "simple") {
    return (
      <div
        className={cn(
          "flex w-max max-w-[calc(100vw-2rem)] items-center gap-2 rounded-xl border p-6 shadow-lg",
          config.container,
          className
        )}
      >
        {renderIcon()}

        <span className={cn("shrink-0 whitespace-nowrap", config.title)}>
          {title}
        </span>
        {description && (
          <span className={cn("whitespace-nowrap", config.description)}>
            {description}
          </span>
        )}

        {withXIcon && onDismiss && (
          <button
            onClick={onDismiss}
            className={cn(
              "ml-auto flex-shrink-0 transition-opacity hover:opacity-70",
              config.title
            )}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  }
}

interface AlertToastOptions {
  iconComponent?: React.ReactNode;
  withXIcon?: boolean;
  variant?: AlertVariant;
  style?: AlertStyle;
  type?: AlertType;
  title: string;
  description?: string;
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  position?: Position;
}

// Alert utility functions to show alerts as toasts
export const alert = {
  show: (options: AlertToastOptions) => {
    const { duration, position = "bottom-center", withXIcon = true, ...alertProps } = options;
    return toast.custom(
      (t) => <Alert {...alertProps} withXIcon={withXIcon} onDismiss={() => toast.dismiss(t)} />,
      {
        position,
        duration:
          options.primaryAction || options.secondaryAction
            ? Infinity
            : duration ?? 5000,
        // Override Sonner's default toast wrapper styles
        className: "!p-0 !border-0 !shadow-none !bg-transparent !min-w-fit",
      }
    );
  },

  // Dismiss alert
  dismiss: toast.dismiss,
};

export type { AlertVariant, AlertStyle, AlertType, AlertToastOptions };
